import React, { useSyncExternalStore } from "react";
import styled from "styled-components";
import { Status, Table } from "@cinesys/ui/components";
import CopyButton from "../../../components/CopyButton";
import { Mono } from "./ui";

// Names copied during this page load; resets on reload.
const copiedNames = new Set<string>();
let copiedVersion = 0;
const listeners = new Set<() => void>();

export const markCopied = (names: string[]) => {
  names.forEach((n) => copiedNames.add(n));
  copiedVersion += 1;
  listeners.forEach((l) => l());
};

const useCopiedVersion = () =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    () => copiedVersion
  );

const Name = styled(Mono)<{ $copied: boolean }>`
  opacity: ${(p) => (p.$copied ? 0.4 : 1)};
`;

// Notes shrink and ellipsize so the file name is never the part that gets cut.
const Note = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* The kit's Status is a flex box (no ellipsis), and failedNote wraps it in a span; make both
     plain blocks so the text can truncate. */
  > *,
  > * > * {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export interface NameListItem {
  key: string;
  text: string;
  note?: React.ReactNode;
}

const ROW_HEIGHT = 37;

/** One name per row with a copy button. */
const NameList = ({
  items,
  maxHeight,
  testId,
}: {
  items: NameListItem[];
  maxHeight?: string;
  testId?: string;
}) => {
  useCopiedVersion();
  const noteFor = (i: NameListItem) =>
    copiedNames.has(i.text) ? <Status type="success">Copied</Status> : i.note;
  const hasNotes = items.some((i) => noteFor(i));

  return (
    <Table
      columns={[
        { width: hasNotes ? "max-content" : "1fr" },
        ...(hasNotes ? [{ width: "minmax(0, 1fr)" }] : []),
        { width: "36px" },
      ]}
      columnGrid
      rowHeight={ROW_HEIGHT}
      maxHeight={maxHeight}
      rows={items.map((i) => ({
        id: i.key,
        element: () => (
          <>
            <Name data-testid={testId} $copied={copiedNames.has(i.text)}>
              {i.text}
            </Name>
            {hasNotes && <Note>{noteFor(i)}</Note>}
            <CopyButton
              value={i.text}
              size={30}
              iconSize={14}
              onCopy={() => markCopied([i.text])}
            />
          </>
        ),
      }))}
    />
  );
};

export default NameList;
